import { Roles, User } from '@gms/user-api';
import {
  createEmptyLoadingState,
  createErrorState,
  createInitialDataState,
  createLoadingState,
  createSuccessState,
} from 'app/store/app/app.models';
import { read, RoleWithPermission } from 'shared/services/roles.service';
import {
  AuthActions,
  EAuthActions,
  FetchMfaOptionsFailure,
  FetchMfaOptionsSuccess,
  InitiateTotpEnrollment,
  InitiateTotpEnrollmentFailure,
  InitiateTotpEnrollmentSuccess,
  ClearRememberedDevice,
  RememberDeviceFailure,
  RememberDeviceSuccess,
  SelectLoginMfaMethod,
  SelectLoginMfaMethodFailure,
  SelectLoginMfaMethodSuccess,
  SwitchMfaMethod,
  SwitchMfaMethodFailure,
  SwitchMfaMethodSuccess,
  VerifyTotpEnrollment,
  VerifyTotpEnrollmentFailure,
  VerifyTotpEnrollmentSuccess,
} from './auth.actions';
import { IAuthState, initialAuthState, MfaInfo, RouteRoleConfigs } from './auth.state';

const ONE_MINUTE = 60000;

const getTokenExpirationDate = (accessToken: string): Date => {
  return new Date(JSON.parse(atob(accessToken.split('.')[1])).exp * 1000);
};

export function buildRoles(roles: RoleWithPermission[]): RoleWithPermission[] {
  const impliedRoles: RoleWithPermission[] = [];
  roles.forEach(role => {
    const [baseRole, accessLevel] = role.split('.');
    if (accessLevel === 'write') {
      impliedRoles.push(read(baseRole as Roles));
    }
  });
  return [...roles, ...impliedRoles].sort();
}

export function authReducers(state = initialAuthState, action: AuthActions): IAuthState {
  let stateUpdates: Partial<IAuthState> = {};

  switch (action.type) {
    case EAuthActions.LOG_IN:
      stateUpdates = {
        accessToken: null,
        roles: [],
        loginLoading: true,
        error: null,
        isAuthenticated: false,
        logoutTime: null,
        refreshToken: null,
        sessionDeviceKey: null,
        saveUserId: action.payload.saveUserId,
        user: createInitialDataState(),
        userId: action.payload.userId,
        warningLogoutTime: null,
        authResources: {},
        isInternal: null,
      };
      break;

    case EAuthActions.LOG_IN_SUCCESS:
      stateUpdates = {
        accessToken: action.payload.accessToken,
        cognitoAccessToken: action.payload.cognitoAccessToken ?? '',
        isADFSLogin: action.payload.isADFSLogin,
        roles: buildRoles(action.payload.roles),
        loginLoading: false,
        error: null,
        isAuthenticated: true,
        refreshToken: action.payload.refreshToken,
        sessionDeviceKey: action.payload.isADFSLogin ? null : action.payload.sessionDeviceKey ?? action.payload.deviceKey ?? null,
        ttl: action.payload.ttl * 1000,
        authResources: action.payload.authResources,
        isInternal: action.payload.isInternal,
        mfa: createSuccessState(null),
        user: createEmptyLoadingState(),
        rememberDevices: action.payload.deviceKey
          ? {
              ...state.rememberDevices,
              [action.payload.loginUsername ?? action.payload.userId]: {
                deviceKey: action.payload.deviceKey,
                deviceGroupKey:
                  action.payload.deviceGroupKey ??
                  (state.rememberDevices[action.payload.loginUsername ?? action.payload.userId]?.deviceKey === action.payload.deviceKey
                    ? state.rememberDevices[action.payload.loginUsername ?? action.payload.userId]?.deviceGroupKey
                    : undefined) ??
                  '',
                devicePassword:
                  action.payload.devicePassword ??
                  (state.rememberDevices[action.payload.loginUsername ?? action.payload.userId]?.deviceKey === action.payload.deviceKey
                    ? state.rememberDevices[action.payload.loginUsername ?? action.payload.userId]?.devicePassword
                    : undefined) ??
                  '',
                // Stays unconfirmed until REMEMBER_DEVICE_SUCCESS; only a re-issue of the same key keeps it confirmed.
                confirmed:
                  state.rememberDevices[action.payload.loginUsername ?? action.payload.userId]?.deviceKey === action.payload.deviceKey &&
                  (state.rememberDevices[action.payload.loginUsername ?? action.payload.userId]?.confirmed ?? false),
              },
            }
          : state.rememberDevices,
        showRememberDevicePrompt: !!action.payload.deviceKey && !state.rememberDevices[action.payload.loginUsername ?? action.payload.userId]?.deviceKey,
      };
      break;

    case EAuthActions.LOG_IN_FAILURE:
      stateUpdates = {
        isAuthenticated: false,
        loginLoading: false,
        error: action.payload.error,
        logoutTime: null,
        warningLogoutTime: null,
        authResources: {},
        isInternal: null,
      };
      break;

    case EAuthActions.REDIRECT_TO_MFA:
      stateUpdates = {
        mfa: createSuccessState({
          ...action.payload,
          username: action.payload.username ?? '',
          mfaChallengeInfo: {
            ...action.payload.mfaChallengeInfo,
            phoneLastFour: action.payload.phoneLastFour ?? null,
            deviceKey: action.payload.deviceKey ?? null,
            deviceGroupKey: action.payload.deviceGroupKey ?? null,
            devicePassword: action.payload.devicePassword ?? null,
          },
        }),
        loginLoading: false,
        error: null,
        isAuthenticated: false,
      };
      break;

    case EAuthActions.LOG_IN_MFA:
      stateUpdates = {
        mfa: createLoadingState(state.mfa),
      };
      break;

    case EAuthActions.LOG_IN_MFA_ERROR:
      stateUpdates = {
        mfa: createErrorState(action.payload.error),
      };
      break;

    case EAuthActions.FETCH_USER_SUCCESS:
      stateUpdates = {
        user: createSuccessState(action.payload.user),
        error: null,
      };
      break;

    case EAuthActions.FETCH_USER_FAILURE:
      stateUpdates = {
        user: createErrorState(action.payload.error),
        error: action.payload.error,
      };
      break;

    case EAuthActions.REFRESH:
      stateUpdates = {
        accessToken: null,
        error: null,
      };
      break;

    case EAuthActions.REFRESH_SUCCESS:
      console.error(
        `Token refresh success, expires ${getTokenExpirationDate(action.payload.accessToken)}`
      );
      stateUpdates = {
        accessToken: action.payload.accessToken,
        error: null,
        isAuthenticated: true,
        ttl: action.payload.ttl * 1000,
      };
      break;

    case EAuthActions.REFRESH_FAILURE:
      console.error('Token refresh failure');
      stateUpdates = {
        isAuthenticated: false,
        user: createInitialDataState(),
        error: action.payload.error,
        logoutTime: null,
        warningLogoutTime: null,
      };
      break;

    case EAuthActions.REFRESH_CANCEL:
      stateUpdates = {
        isAuthenticated: false,
        user: createInitialDataState(),
        error: null,
        logoutTime: null,
        warningLogoutTime: null,
      };
      break;

    case EAuthActions.LOG_OUT:
      stateUpdates = {
        sessionDeviceKey: null,
        accessToken: null,
        isADFSLogin: false,
        roles: [],
        error: null,
        isAuthenticated: false,
        logoutTime: null,
        refreshToken: null,
        user: createInitialDataState(),
        warningLogoutTime: null,
        authResources: {},
        mfa: createInitialDataState(),
        totpEnrollment: {
          provisioningUri: null,
          secretCode: null,
          session: null,
          enrollAccessToken: null,
          verified: false,
          requestStatus: {
            loading: false,
            httpError: null,
          },
        },
        mfaOptions: {
          data: null,
          requestStatus: {
            loading: false,
            httpError: null,
          },
        },
        showRememberDevicePrompt: false,
        rememberDeviceRequestStatus: {
          loading: false,
          httpError: null,
        },
      };
      break;

    // used for refresh failures
    case EAuthActions.LOG_OUT_WITH_ERROR:
      console.error('Token refresh failure');
      stateUpdates = {
        sessionDeviceKey: null,
        accessToken: null,
        isADFSLogin: false,
        roles: [],
        error: action.payload.error,
        isAuthenticated: false,
        logoutTime: null,
        refreshToken: null,
        user: createInitialDataState(),
        warningLogoutTime: null,
        authResources: {},
        mfa: createInitialDataState(),
        totpEnrollment: {
          provisioningUri: null,
          secretCode: null,
          session: null,
          enrollAccessToken: null,
          verified: false,
          requestStatus: {
            loading: false,
            httpError: null,
          },
        },
        mfaOptions: {
          data: null,
          requestStatus: {
            loading: false,
            httpError: null,
          },
        },
        showRememberDevicePrompt: false,
        rememberDeviceRequestStatus: {
          loading: false,
          httpError: null,
        },
      };
      break;

    case EAuthActions.FORGET_USER_ID:
      stateUpdates = {
        saveUserId: false,
      };
      break;

    case EAuthActions.ACCEPT_COOKIES:
      stateUpdates = {
        cookiesAccepted: true,
      };
      break;

    case EAuthActions.UPDATE_PASSWORD:
      stateUpdates = {
        updatePasswordLoading: true,
        updatePasswordError: null,
      };
      break;

    case EAuthActions.UPDATE_PASSWORD_SUCCESS:
      stateUpdates = {
        updatePasswordLoading: false,
        updatePasswordError: null,
      };
      break;

    case EAuthActions.UPDATE_PASSWORD_FAILURE:
      stateUpdates = {
        updatePasswordLoading: false,
        updatePasswordError: action.payload.error,
      };
      break;

    case EAuthActions.FETCH_ROUTE_CONFIGS:
      stateUpdates = {
        routeRoleConfigsLoading: true,
      };
      break;

    case EAuthActions.FETCH_ROUTE_CONFIGS_SUCCESS:
      stateUpdates = {
        routeRoleConfigs: action.payload.rolesPages.reduce(
          (acc, config): RouteRoleConfigs => {
            if (config.uiRoute) {
              acc[config.uiRoute] = config.roles ?? [];
            }
            return acc;
          },
          {} as RouteRoleConfigs
        ),
        routeRoleConfigsLoading: false,
      };
      break;

    case EAuthActions.FETCH_ROUTE_CONFIGS_FAILURE:
      stateUpdates = {
        routeRoleConfigsLoading: false,
        routeRoleConfigsError: action.payload.error,
      };
      break;

    case EAuthActions.SSO_REDIRECT:
      stateUpdates = {
        saveUserId: action.payload.saveUserId,
        userId: action.payload.userId,
      };
      break;

    case EAuthActions.SSO_LOG_IN:
      stateUpdates = {
        accessToken: null,
        roles: [],
        loginLoading: true,
        error: null,
        isAuthenticated: false,
        logoutTime: null,
        refreshToken: null,
        user: createInitialDataState(),
        warningLogoutTime: null,
        authResources: {},
      };
      break;

    case EAuthActions.SSO_LOG_IN_FAILURE:
      stateUpdates = {
        isAuthenticated: false,
        loginLoading: false,
        error: action.payload.error,
        logoutTime: null,
        warningLogoutTime: null,
        authResources: {},
      };
      break;

    case EAuthActions.UNAUTHORIZED_USER:
      stateUpdates = {
        accessToken: null,
        isAuthenticated: false,
        roles: [],
        error: null,
        logoutTime: null,
        refreshToken: null,
        warningLogoutTime: null,
        authResources: {},
      };
      break;

    case EAuthActions.INVALID_SSO_USER_ID:
      stateUpdates = {
        invalidSsoUserId: true,
        userId: action.payload.userId,
      };
      break;
    case EAuthActions.FETCH_WEBSOCKET_TICKET:
      stateUpdates = {
        websocketTicket: createEmptyLoadingState(),
      };
      break;
    case EAuthActions.FETCH_WEBSOCKET_TICKET_SUCCESS:
      stateUpdates = {
        websocketTicket: createSuccessState(action.payload.ticket.ticket),
      };
      break;
    case EAuthActions.FETCH_WEBSOCKET_TICKET_FAILURE:
      stateUpdates = {
        websocketTicket: createErrorState(action.payload.error),
      };
      break;

    case EAuthActions.SWITCH_MFA_METHOD:
      stateUpdates = {
        mfa: createLoadingState(state.mfa),
      };
      break;

    case EAuthActions.SWITCH_MFA_METHOD_SUCCESS: {
      const switchSuccessAction = action as SwitchMfaMethodSuccess;
      stateUpdates = {
        mfa: createSuccessState({
          ...state.mfa.data,
          mfaChallengeInfo: {
            ...(state.mfa.data?.mfaChallengeInfo ?? {}),
            sessionId: switchSuccessAction.payload.sessionId,
            challengeName: switchSuccessAction.payload.challengeName,
            deliveryDestination: switchSuccessAction.payload.deliveryDestination,
            availableMethods: switchSuccessAction.payload.availableMethods,
            phoneLastFour: switchSuccessAction.payload.phoneLastFour ?? null,
          },
        } as MfaInfo),
      };
      break;
    }

    case EAuthActions.SWITCH_MFA_METHOD_FAILURE: {
      const switchFailureAction = action as SwitchMfaMethodFailure;
      stateUpdates = {
        mfa: { data: state.mfa.data, requestStatus: { loading: false, httpError: switchFailureAction.payload.error } },
      };
      break;
    }

    case EAuthActions.SELECT_LOGIN_MFA_METHOD:
      stateUpdates = {
        mfa: createLoadingState(state.mfa),
      };
      break;

    case EAuthActions.SELECT_LOGIN_MFA_METHOD_SUCCESS: {
      const selectSuccessAction = action as SelectLoginMfaMethodSuccess;
      stateUpdates = {
        mfa: createSuccessState({
          ...state.mfa.data,
          mfaChallengeInfo: {
            ...(state.mfa.data?.mfaChallengeInfo ?? {}),
            sessionId: selectSuccessAction.payload.sessionId,
            challengeName: selectSuccessAction.payload.challengeName,
            deliveryDestination: selectSuccessAction.payload.deliveryDestination,
            availableMethods: selectSuccessAction.payload.availableMethods,
            phoneLastFour: selectSuccessAction.payload.phoneLastFour ?? null,
            deviceKey: selectSuccessAction.payload.deviceKey != null ? selectSuccessAction.payload.deviceKey : state.mfa.data?.mfaChallengeInfo?.deviceKey,
            deviceGroupKey: selectSuccessAction.payload.deviceGroupKey != null ? selectSuccessAction.payload.deviceGroupKey : state.mfa.data?.mfaChallengeInfo?.deviceGroupKey,
            devicePassword: selectSuccessAction.payload.devicePassword != null ? selectSuccessAction.payload.devicePassword : state.mfa.data?.mfaChallengeInfo?.devicePassword,
          },
        } as MfaInfo),
      };
      break;
    }

    case EAuthActions.SELECT_LOGIN_MFA_METHOD_FAILURE: {
      const selectFailureAction = action as SelectLoginMfaMethodFailure;
      stateUpdates = {
        mfa: { data: state.mfa.data, requestStatus: { loading: false, httpError: selectFailureAction.payload.error } },
      };
      break;
    }

    case EAuthActions.RESET_TOTP_ENROLLMENT:
      stateUpdates = {
        totpEnrollment: {
          provisioningUri: null,
          secretCode: null,
          session: null,
          enrollAccessToken: null,
          verified: false,
          requestStatus: { loading: false, httpError: null },
        },
      };
      break;

    case EAuthActions.INITIATE_TOTP_ENROLLMENT:
      stateUpdates = {
        totpEnrollment: {
          provisioningUri: null,
          secretCode: null,
          session: null,
          enrollAccessToken: null,
          verified: false,
          requestStatus: { loading: true, httpError: null },
        },
      };
      break;

    case EAuthActions.INITIATE_TOTP_ENROLLMENT_SUCCESS: {
      const initiateSuccessAction = action as InitiateTotpEnrollmentSuccess;
      stateUpdates = {
        totpEnrollment: {
          provisioningUri: initiateSuccessAction.payload.provisioningUri,
          secretCode: initiateSuccessAction.payload.secretCode ?? null,
          session: initiateSuccessAction.payload.session ?? null,
          enrollAccessToken: initiateSuccessAction.payload.enrollAccessToken ?? null,
          verified: false,
          requestStatus: { loading: false, httpError: null },
        },
      };
      break;
    }

    case EAuthActions.INITIATE_TOTP_ENROLLMENT_FAILURE: {
      const initiateFailureAction = action as InitiateTotpEnrollmentFailure;
      stateUpdates = {
        totpEnrollment: {
          ...state.totpEnrollment,
          requestStatus: { loading: false, httpError: initiateFailureAction.payload.error },
        },
      };
      break;
    }

    case EAuthActions.VERIFY_TOTP_ENROLLMENT:
      stateUpdates = {
        totpEnrollment: {
          ...state.totpEnrollment,
          requestStatus: { loading: true, httpError: null },
        },
      };
      break;

    case EAuthActions.VERIFY_TOTP_ENROLLMENT_SUCCESS:
      stateUpdates = {
        totpEnrollment: {
          provisioningUri: null,
          secretCode: null,
          session: null,
          enrollAccessToken: null,
          verified: true,
          requestStatus: { loading: false, httpError: null },
        },
      };
      break;

    case EAuthActions.VERIFY_TOTP_ENROLLMENT_FAILURE: {
      const verifyFailureAction = action as VerifyTotpEnrollmentFailure;
      stateUpdates = {
        totpEnrollment: {
          ...state.totpEnrollment,
          verified: false,
          requestStatus: { loading: false, httpError: verifyFailureAction.payload.error },
        },
      };
      break;
    }

    case EAuthActions.FETCH_MFA_OPTIONS:
      stateUpdates = {
        mfaOptions: {
          data: state.mfaOptions?.data ?? null,
          requestStatus: { loading: true, httpError: null },
        },
      };
      break;

    case EAuthActions.FETCH_MFA_OPTIONS_SUCCESS: {
      const fetchMfaOptionsSuccessAction = action as FetchMfaOptionsSuccess;
      stateUpdates = {
        mfaOptions: {
          data: fetchMfaOptionsSuccessAction.payload.mfaOptions,
          requestStatus: { loading: false, httpError: null },
        },
      };
      break;
    }

    case EAuthActions.FETCH_MFA_OPTIONS_FAILURE: {
      const fetchMfaOptionsFailureAction = action as FetchMfaOptionsFailure;
      stateUpdates = {
        mfaOptions: {
          data: null,
          requestStatus: { loading: false, httpError: fetchMfaOptionsFailureAction.payload.error },
        },
      };
      break;
    }

    case EAuthActions.REMEMBER_DEVICE:
      stateUpdates = {
        rememberDeviceRequestStatus: { loading: true, httpError: null },
      };
      break;

    case EAuthActions.REMEMBER_DEVICE_SUCCESS: {
      const rememberDeviceSuccessAction = action as RememberDeviceSuccess;
      const username = rememberDeviceSuccessAction.payload?.username;
      stateUpdates = {
        showRememberDevicePrompt: false,
        rememberDeviceRequestStatus: { loading: false, httpError: null },
        ...(username && state.rememberDevices[username] ? {
          rememberDevices: {
            ...state.rememberDevices,
            [username]: { ...state.rememberDevices[username], confirmed: true },
          },
        } : {}),
      };
      break;
    }

    case EAuthActions.REMEMBER_DEVICE_FAILURE: {
      const rememberDeviceFailureAction = action as RememberDeviceFailure;
      stateUpdates = {
        rememberDeviceRequestStatus: { loading: false, httpError: rememberDeviceFailureAction.payload.error },
      };
      break;
    }

    case EAuthActions.CLEAR_REMEMBERED_DEVICE: {
      const clearDeviceAction = action as ClearRememberedDevice;
      const { [clearDeviceAction.payload.userId]: _, ...remainingDevices } = state.rememberDevices;
      stateUpdates = {
        rememberDevices: remainingDevices,
      };
      break;
    }

    case EAuthActions.CLEAR_MFA_ERROR:
      stateUpdates = {
        mfa: {
          ...state.mfa,
          requestStatus: { ...state.mfa.requestStatus, httpError: null, loading: false },
        },
      };
      break;

    case EAuthActions.LOCK:
      stateUpdates = {
        accessToken: null,
        refreshToken: null,
        isAuthenticated: false,
      };
      break;
  }

  return {
    ...state,
    ...stateUpdates,
  };
}
